// JoPLogo.jsx
// Wraps a JoP logo image with size + optional pulse animation.
// Two logo variants:
//   "stream"  → /jop_stream.png (compact, used in Main overlay top bar)
//   "wide"    → /jop_logo_wide.png (full pancake stack, used on customer
//                screens, Starting Soon, Break, Outro)
//
// Pulse implementation note: the breathing glow is a duplicate logo stacked
// behind the crisp one, carrying a STATIC drop-shadow (rasterized once and
// cached) whose OPACITY oscillates. Opacity is a compositor-only property, so
// the pulse costs ~nothing per frame. The previous version animated the
// drop-shadow *value* (12px→28px), which re-rasterized the filter every frame
// on the GPU compositor thread — the confirmed heat source. Visually identical.
import useObsActive from "../../hooks/useObsActive";

const PULSE_KEY = "jopLogoGlowPulse";

const LOGO_SRCS = {
  stream: "/jop_stream.png",
  wide:   "/jop_logo_wide.png",
};

export default function JoPLogo({
  variant = "stream",
  height = 80,
  animated = false,
  style = {},
  alt = "Joy of Pancakes",
}) {
  const src = LOGO_SRCS[variant] || LOGO_SRCS.stream;
  const active = useObsActive();
  const h = typeof height === "number" ? `${height}px` : height;

  if (!animated) {
    return (
      <img
        src={src}
        alt={alt}
        style={{ height: h, objectFit: "contain", flexShrink: 0, ...style }}
      />
    );
  }

  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0, ...style }}>
      <style>{`
        @keyframes ${PULSE_KEY} {
          0%, 100% { opacity: 0.45; }
          50%      { opacity: 0.9; }
        }
      `}</style>
      {/* Glow layer: static filter (baked once), opacity breathes (free). */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          height: h,
          width: "auto",
          objectFit: "contain",
          filter: "drop-shadow(0 0 16px rgba(var(--jop-gold-rgb), 0.85)) brightness(1.3)",
          animation: `${PULSE_KEY} 2.4s ease-in-out infinite`,
          animationPlayState: active ? "running" : "paused",
          pointerEvents: "none",
        }}
      />
      {/* Crisp logo on top. */}
      <img
        src={src}
        alt={alt}
        style={{ height: h, objectFit: "contain", position: "relative" }}
      />
    </span>
  );
}
